import React, { useState, useEffect } from 'react';
import { fetchWithAuth, REGISTRATIONS_API, EVENTS_API, USERS_API, api } from '../../utils/api';
import { Download, Calendar, Music, Users, FileAudio, Clock } from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { fetchAuthSession, fetchUserAttributes } from '@aws-amplify/auth';
import { useTheme } from "../../context/ThemeContext";

// Tab components
import RegistrationsTab from './competition/RegistrationsTab';
import RoutinesTab from './competition/RoutinesTab';
import AudioTab from './competition/AudioTab';
import ScheduleTab from './competition/ScheduleTab';

const CompetitionManager = () => {
  // Initialize with a valid tab value
  const [activeTab, setActiveTab] = useState('registrations');
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [userDetails, setUserDetails] = useState({}); // Store user details by userId
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  // Log active tab changes
  useEffect(() => {
    console.log('Active tab changed to:', activeTab);
  }, [activeTab]);
  
  // Fetch events on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(EVENTS_API);
        if (response && response.events) {
          setEvents(response.events);
          // If there are events, select the first one by default
          if (response.events.length > 0) {
            setSelectedEvent(response.events[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Failed to fetch events. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvents();
  }, []);
  
  // Fetch registrations when selected event changes
  useEffect(() => {
    const fetchRegistrations = async () => {
      if (!selectedEvent) return;
      
      try {
        setLoading(true);
        console.log(`Fetching registrations for event: ${selectedEvent.eventId}`);
        const response = await fetchWithAuth(`${REGISTRATIONS_API}?eventId=${selectedEvent.eventId}`);
        console.log('Registration API response:', response);
        
        if (response && response.registrations) {
          console.log(`Found ${response.registrations.length} registrations`);
          
          // Process registrations to ensure they have all required fields
          const processedRegistrations = response.registrations.map(reg => {
            // Ensure we have a studioName
            if (!reg.studioName) {
              // Try to get studio name from other fields if available
              reg.studioName = reg.studio?.name || 
                              reg.studioInfo?.name || 
                              reg.contactName || 
                              'Unknown Studio';
            }
            
            // Ensure dancers, teachers, observers arrays exist
            reg.dancers = reg.dancers || [];
            reg.teachers = reg.teachers || [];
            reg.observers = reg.observers || [];
            
            // Ensure competition and routines exist
            reg.competition = reg.competition || {};
            reg.competition.routines = reg.competition.routines || [];
            
            return reg;
          });
          
          console.log('Processed registrations:', processedRegistrations);
          setRegistrations(processedRegistrations);
          
          // Extract routines from registrations
          const allRoutines = [];
          processedRegistrations.forEach(reg => {
            console.log('Processing registration:', reg);
            if (reg.competition && reg.competition.routines && reg.competition.routines.length > 0) {
              console.log(`Found ${reg.competition.routines.length} routines in registration`);
              
              reg.competition.routines.forEach(routine => {
                // Generate a unique ID if one doesn't exist
                const routineId = routine.id || routine.routineId || `routine-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                
                // Process the routine to ensure it has all required fields
                const processedRoutine = {
                  ...routine,
                  id: routineId,
                  registrationId: reg.registrationId,
                  userId: reg.userId,
                  studioName: reg.studioName || 'Unknown Studio',
                  // Use title field if available, otherwise use name or default
                  name: routine.title || routine.name || 'Untitled Routine',
                  category: routine.category || 'Uncategorized',
                  ageGroup: routine.ageGroup || routine.age || 'All Ages',
                  level: routine.level || 'All Levels',
                  duration: routine.duration || 0,
                  dancers: routine.dancers || [],
                  // Use songFileUrl for audioUrl if available
                  audioUrl: routine.songFileUrl || routine.audioUrl || null,
                  // Add songFileName if available
                  songFileName: routine.songFileName || null
                };
                
                allRoutines.push(processedRoutine);
              });
            } else {
              console.log('No competition routines found in this registration');
            }
          });
          
          console.log(`Total routines extracted: ${allRoutines.length}`);
          console.log('Processed routines:', allRoutines);
          setRoutines(allRoutines);
          
          // Fetch user details for each registration
          const userIds = [...new Set(processedRegistrations.map(reg => reg.userId))];
          console.log('Unique userIds to fetch:', userIds);
          
          const fetchedUserDetails = {};
          
          // Fetch user details directly from the Users table
          await Promise.all(
            userIds.map(async (userId) => {
              try {
                if (userId) {
                  console.log(`Fetching user details for userId: ${userId}`);
                  
                  // Use the direct method to get user details
                  const userDetails = await api.getUserDirectly(userId);
                  console.log(`User details for ${userId}:`, userDetails);
                  
                  if (userDetails) {
                    fetchedUserDetails[userId] = userDetails;
                  } else {
                    // If we couldn't get user details, create a placeholder
                    fetchedUserDetails[userId] = { 
                      userId: userId,
                      firstName: "User",
                      lastName: userId.substring(0, 8) // Use part of the userId as a last name
                    };
                  }
                }
              } catch (err) {
                console.error(`Error fetching user details for ${userId}:`, err);
                // Create a placeholder on error
                fetchedUserDetails[userId] = { 
                  userId: userId,
                  firstName: "User",
                  lastName: userId.substring(0, 8) // Use part of the userId as a last name
                };
              }
            })
          );
          
          console.log('Fetched user details:', fetchedUserDetails);
          setUserDetails(fetchedUserDetails);
        } else {
          console.log('No registrations found in the response');
          setRegistrations([]);
          setRoutines([]);
        }
      } catch (err) {
        console.error('Error fetching registrations:', err);
        setError('Failed to fetch registrations. Please try again.');
        setRegistrations([]);
        setRoutines([]);
      } finally {
        // Ensure loading is set to false after data is processed
        console.log('Setting loading to false');
        setLoading(false);
      }
    };
    
    fetchRegistrations();
  }, [selectedEvent]);
  
  // Handle event selection change
  const handleEventChange = (e) => {
    const eventId = e.target.value;
    const event = events.find(evt => evt.eventId === eventId);
    setSelectedEvent(event);
  };
  
  // Handle tab change explicitly
  const handleTabChange = (value) => {
    console.log('Tab change handler called with:', value);
    setActiveTab(value);
  };
  
  // Download all audio files as a zip
  const downloadAllAudio = async () => {
    if (!routines.length) {
      alert('No routines with audio files available');
      return;
    }
    
    try {
      setLoading(true);
      
      const zip = new JSZip();
      const audioFolder = zip.folder('audio_files');
      
      // Add each audio file to the zip
      const audioPromises = routines
        .filter(routine => routine.audioUrl)
        .map(async (routine, index) => {
          try {
            const response = await fetch(routine.audioUrl);
            const blob = await response.blob();
            
            // Create filename with routine details
            // Use songFileName if available, otherwise create a name from routine details
            const fileName = routine.songFileName || 
              `${index + 1}_${routine.studioName}_${routine.name}_${routine.category || 'No Category'}.mp3`;
            
            audioFolder.file(fileName, blob);
            return true;
          } catch (err) {
            console.error(`Error downloading audio for routine ${routine.name}:`, err);
            return false;
          }
        });
      
      await Promise.all(audioPromises);
      
      // Generate and save the zip file
      const content = await zip.generateAsync({ type: 'blob' });
      const eventName = selectedEvent ? selectedEvent.city.replace(/\s+/g, '_') : 'event';
      saveAs(content, `${eventName}_audio_files.zip`);
      
    } catch (err) {
      console.error('Error creating zip file:', err);
      setError('Failed to download audio files. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && !selectedEvent) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Competition Management</h1>
          <p className="text-base-content/70">
            Manage registrations, routines, audio files, and competition schedules
          </p>
        </div>
        
        {/* Event selector */}
        <div className="form-control w-full md:w-64">
          <select 
            className="select select-bordered w-full"
            value={selectedEvent?.eventId || ''}
            onChange={handleEventChange}
            disabled={loading || events.length === 0}
          >
            {events.length === 0 ? (
              <option value="" disabled>No events available</option>
            ) : (
              events.map(event => (
                <option key={event.eventId} value={event.eventId}>
                  {event.city} - {new Date(event.startDate).toLocaleDateString()}
                </option>
              ))
            )}
          </select>
        </div>
      </div>
      
      {/* Debug information */}
      <div className={`p-4 rounded-lg ${
        isDarkMode 
          ? 'bg-[#131620] border border-white/5' 
          : 'bg-base-200'
      }`}>
        <h3 className="font-medium mb-2">Debug Information</h3>
        <p>Loading: {loading ? 'Yes' : 'No'}</p>
        <p>Selected Event: {selectedEvent ? selectedEvent.city : 'None'}</p>
        <p>Registrations count: {registrations.length}</p>
        <p>Routines count: {routines.length}</p>
        <p>Error: {error || 'None'}</p>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="alert alert-error shadow-lg">
          <span>{error}</span>
          <button 
            className="btn btn-sm btn-ghost"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Main content */}
      {selectedEvent ? (
        <div className={`card shadow-xl ${
          isDarkMode 
            ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' 
            : 'bg-base-100'
        }`}>
          <div className="card-body">
            {/* Custom tabs implementation */}
            <div className="tabs-container">
              {/* Tab buttons */}
              <div className="flex border-b border-base-300 mb-6">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'registrations'}
                  onClick={() => handleTabChange('registrations')}
                  className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'registrations'
                      ? "text-primary border-b-2 border-primary"
                      : "text-base-content/70 hover:text-base-content"
                  }`}
                >
                  <Users className="h-4 w-4" />
                  <span>Registrations</span>
                </button>
                
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'routines'}
                  onClick={() => handleTabChange('routines')}
                  className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'routines'
                      ? "text-primary border-b-2 border-primary"
                      : "text-base-content/70 hover:text-base-content"
                  }`}
                >
                  <Music className="h-4 w-4" />
                  <span>Routines</span>
                </button>
                
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'audio'}
                  onClick={() => handleTabChange('audio')}
                  className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'audio'
                      ? "text-primary border-b-2 border-primary"
                      : "text-base-content/70 hover:text-base-content"
                  }`}
                >
                  <FileAudio className="h-4 w-4" />
                  <span>Audio</span>
                </button>
                
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'schedule'}
                  onClick={() => handleTabChange('schedule')}
                  className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'schedule'
                      ? "text-primary border-b-2 border-primary"
                      : "text-base-content/70 hover:text-base-content"
                  }`}
                >
                  <Clock className="h-4 w-4" />
                  <span>Schedule</span>
                </button>
              </div>
              
              {/* Tab content */}
              {activeTab === 'registrations' && (
                <div>
                  <div className={`p-2 rounded mb-2 text-xs ${
                    isDarkMode 
                      ? 'bg-[#131620] border border-white/5' 
                      : 'bg-base-200 border'
                  }`}>
                    Active Tab: {activeTab} | Registrations Count: {registrations.length} | Loading: {loading ? 'Yes' : 'No'}
                  </div>
                  <RegistrationsTab 
                    registrations={registrations} 
                    event={selectedEvent} 
                    loading={loading}
                    userDetails={userDetails}
                  />
                </div>
              )}
              
              {activeTab === 'routines' && (
                <div>
                  <div className={`p-2 rounded mb-2 text-xs ${
                    isDarkMode 
                      ? 'bg-[#131620] border border-white/5' 
                      : 'bg-base-200 border'
                  }`}>
                    Active Tab: {activeTab} | Routines Count: {routines.length} | Loading: {loading ? 'Yes' : 'No'}
                  </div>
                  <RoutinesTab 
                    routines={routines} 
                    registrations={registrations}
                    event={selectedEvent} 
                    loading={loading}
                    userDetails={userDetails}
                  />
                </div>
              )}
              
              {activeTab === 'audio' && (
                <div>
                  <div className={`p-2 rounded mb-2 text-xs ${
                    isDarkMode 
                      ? 'bg-[#131620] border border-white/5' 
                      : 'bg-base-200 border'
                  }`}>
                    Active Tab: {activeTab} | Routines Count: {routines.length} | Loading: {loading ? 'Yes' : 'No'}
                  </div>
                  <AudioTab 
                    routines={routines} 
                    registrations={registrations}
                    event={selectedEvent} 
                    loading={loading}
                    downloadAllAudio={downloadAllAudio}
                    userDetails={userDetails}
                  />
                </div>
              )}
              
              {activeTab === 'schedule' && (
                <div>
                  <div className={`p-2 rounded mb-2 text-xs ${
                    isDarkMode 
                      ? 'bg-[#131620] border border-white/5' 
                      : 'bg-base-200 border'
                  }`}>
                    Active Tab: {activeTab} | Routines Count: {routines.length} | Loading: {loading ? 'Yes' : 'No'}
                  </div>
                  <ScheduleTab 
                    routines={routines} 
                    registrations={registrations}
                    event={selectedEvent} 
                    loading={loading}
                    userDetails={userDetails}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className={`card shadow-xl ${
          isDarkMode 
            ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' 
            : 'bg-base-100'
        }`}>
          <div className="card-body">
            <div className="text-center py-8">
              <h3 className="text-lg font-medium">No Event Selected</h3>
              <p className="text-base-content/70 mt-2">
                Please select an event from the dropdown above to manage competition details.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Debug section for user details */}
      {process.env.NODE_ENV === 'development' && (
        <div className={`debug-section ${
          isDarkMode 
            ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' 
            : 'bg-base-100 shadow'
        }`} style={{ margin: '20px 0', padding: '15px', borderRadius: '5px' }}>
          <h3>Debug Information</h3>
          <p>User Details Count: {Object.keys(userDetails).length}</p>
          <details>
            <summary>User Details (Click to expand)</summary>
            <pre style={{ maxHeight: '200px', overflow: 'auto' }}>
              {JSON.stringify(userDetails, null, 2)}
            </pre>
          </details>
          <details>
            <summary>Registrations (Click to expand)</summary>
            <pre style={{ maxHeight: '200px', overflow: 'auto' }}>
              {JSON.stringify(registrations, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default CompetitionManager; 